import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { School, Building2, Landmark, HeartPulse, Users, Church, Shield, Handshake, ArrowRight, Check } from "lucide-react";
import { submitPartnership } from "../lib/api";

const TYPES = [
  { value: "school", icon: School, title: "Schools & Districts", body: "In-school mentoring, prevention education, assemblies, and parent nights." },
  { value: "business", icon: Building2, title: "Local Businesses", body: "Workforce readiness partnerships, mentor volunteers, and in-kind support." },
  { value: "foundation", icon: Landmark, title: "Corporations & Foundations", body: "Program sponsorship, grants, and multi-year community investment." },
  { value: "healthcare", icon: HeartPulse, title: "Healthcare & Mental Health", body: "Clinical referral partnerships and community wellness collaborations." },
  { value: "community", icon: Users, title: "Community Organizations", body: "Joint events, resource sharing, and youth development collaborations." },
  { value: "faith", icon: Church, title: "Faith Communities", body: "Family engagement and volunteer mentor recruitment." },
  { value: "municipal", icon: Shield, title: "Municipal Agencies", body: "Prevention programming, court-adjacent mentoring, and youth outreach." },
  { value: "other", icon: Handshake, title: "Something Else?", body: "If you don't see your category, start a conversation and we'll figure it out together." },
];

export default function Partnerships() {
  const [form, setForm] = useState({
    organization: "", contact_name: "", email: "", phone: "",
    partnership_type: "school", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitPartnership(form);
      toast.success("Thank you! Our team will be in touch within 3 business days.");
      setForm({ organization: "", contact_name: "", email: "", phone: "", partnership_type: "school", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="partner-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Partnership Center</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight">
              Partner with us to <span className="italic text-[#F5B90A]">change this generation.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/80">
              Schools, businesses, foundations, agencies, and community organizations — there's a seat at this table for you.
              Let's build something that outlasts us.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#partnership-form" className="btn-gold"><ArrowRight className="h-4 w-4" /> Request a Meeting</a>
              <Link to="/resources?category=Sponsorship+Packets" className="btn-outline-white">Download Sponsor Packet</Link>
            </div>
          </div>
        </div>
      </section>

      {/* PARTNERSHIP TYPES */}
      <section className="bg-white py-20 md:py-24" data-testid="partner-types">
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="overline text-[#6B21A8]">Who We Partner With</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">A seat for every kind of partner.</h2>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {TYPES.map((t, i) => (
              <div key={t.value} className="card-editorial p-6" data-testid={`partner-type-${t.value}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#6B21A8]/10 text-[#6B21A8]">
                  <t.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold">{t.title}</h3>
                <p className="mt-2 text-sm text-black/70">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[#F7F3EC] py-20" data-testid="partner-why">
        <div className="container-x grid gap-10 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="overline text-[#6B21A8]">Why Partner With Change Center</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">Real programming. Real outcomes. Real community.</h2>
            <p className="mt-5 text-black/70 text-lg">
              We're a 501(c)(3) nonprofit grounded in Clayton County, with signature programming in mental wellness,
              prevention, leadership, and workforce readiness.
            </p>
          </div>
          <div className="md:col-span-7 grid gap-3">
            {[
              "Signature 24-week Change Choices: Strong Minds curriculum",
              "Trained, screened, and background-checked mentors",
              "Family-engaged programming across every track",
              "School-based delivery option with teacher coordination",
              "Transparent impact reporting and verified statistics",
              "Flexible sponsorship tiers for every budget",
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg bg-white p-5 border border-black/5" data-testid={`partner-why-${i}`}>
                <Check className="h-5 w-5 mt-0.5 text-[#6B21A8] flex-shrink-0" />
                <p className="font-medium">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="partnership-form" className="bg-white py-20" data-testid="partner-form-section">
        <div className="container-x grid gap-10 md:grid-cols-12 items-start">
          <div className="md:col-span-5">
            <h2 className="font-display text-4xl md:text-5xl font-bold">Request a partnership meeting.</h2>
            <p className="mt-4 text-black/70">
              Share a few details and our team will reach out within three business days to schedule time together.
            </p>
            <p className="mt-6 text-sm text-black/60">
              Approved sponsors and partners may be recognized on our site after signed agreement. We only display verified partners.
            </p>
          </div>
          <form onSubmit={submit} className="md:col-span-7 grid gap-4 bg-[#F7F3EC] rounded-lg p-8" data-testid="partner-form">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-semibold">Organization *</span>
                <input required value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="partner-org" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold">Contact Name *</span>
                <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="partner-contact" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold">Email *</span>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="partner-email" />
              </label>
              <label className="block">
                <span className="text-sm font-semibold">Phone</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="partner-phone" />
              </label>
            </div>
            <label className="block">
              <span className="text-sm font-semibold">Partnership Type *</span>
              <select value={form.partnership_type} onChange={(e) => setForm({ ...form, partnership_type: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="partner-type">
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.title}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Tell us about your organization and goals</span>
              <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm" data-testid="partner-message" />
            </label>
            <button disabled={submitting} className="btn-primary w-full md:w-auto" data-testid="partner-submit">
              {submitting ? "Sending..." : "Request Meeting"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
